import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { TimeSelect } from "@/components/TimeSelect";
import { MarkdownField } from "@/components/MarkdownField";
import { Markdown } from "@/components/Markdown";
import { api, errText } from "@/lib/api";
import { fmtTime } from "@/lib/eventMeta";
import { useAuth } from "@/context/AuthContext";

const blank = {
  name: "",
  location: "",
  opens_at: "10:00",
  closes_at: "17:00",
  session_1_start: "10:30",
  session_1_end: "13:30",
  session_2_start: "14:30",
  session_2_end: "16:30",
  notes: "",
  active: true,
};

export default function Courts() {
  const { isOwner, workspace, role } = useAuth();
  const [courts, setCourts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    if (role === "clerk") return;
    try {
      const { data } = await api.get("/courts");
      setCourts(data);
    } catch (e) {
      toast.error(errText(e));
    }
  }, [workspace?.workspace_id]);

  useEffect(() => {
    load();
  }, [load]);

  const startEdit = (court) => {
    setEditingId(court.court_id);
    setForm({ ...blank, ...court });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Court name is required");
    const payload = {
      name: form.name.trim(),
      location: form.location || null,
      opens_at: form.opens_at,
      closes_at: form.closes_at,
      session_1_start: form.session_1_start,
      session_1_end: form.session_1_end,
      session_2_start: form.session_2_start,
      session_2_end: form.session_2_end,
      notes: form.notes || null,
      active: true,
    };
    try {
      if (editingId) await api.put(`/courts/${editingId}`, payload);
      else await api.post("/courts", payload);
      toast.success(editingId ? "Court profile updated" : "Court profile created");
      setOpen(false);
      setEditingId(null);
      setForm(blank);
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/courts/${id}`);
      toast.success("Court removed");
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  return (
    <div className="space-y-8" data-testid="courts-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="label-caps">Administration</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold mt-2">Court profiles</h1>
          <p className="mt-3 text-sm text-muted-foreground max-w-xl">
            Session boundaries decide whether a fixed-time listing falls pre-lunch or post-lunch on
            the heat map.
          </p>
        </div>
        {isOwner ? (
          <Button
            data-testid="new-court-btn"
            onClick={() => {
              setEditingId(null);
              setForm(blank);
              setOpen(true);
            }}
            className="rounded-full h-12 px-6 text-base"
          >
            <Plus className="h-5 w-5 mr-2" /> New court
          </Button>
        ) : null}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {courts.map((c, i) => (
          <div
            key={c.court_id}
            data-testid={`court-card-${c.court_id}`}
            className="surface p-6 rise"
            style={{ animationDelay: `${i * 60}ms` }}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-heading font-semibold">{c.name}</h2>
                {c.location ? (
                  <p className="mt-1.5 text-sm text-muted-foreground inline-flex items-center gap-1.5">
                    <MapPin className="h-4 w-4" /> {c.location}
                  </p>
                ) : null}
              </div>
              {isOwner ? (
                <div className="flex gap-1">
                  <button
                    data-testid={`edit-court-${c.court_id}`}
                    onClick={() => startEdit(c)}
                    className="h-9 w-9 grid place-items-center rounded-sm hover:bg-accent transition-colors duration-200"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    data-testid={`delete-court-${c.court_id}`}
                    onClick={() => remove(c.court_id)}
                    className="h-9 w-9 grid place-items-center rounded-sm text-destructive hover:bg-destructive/10 transition-colors duration-200"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ) : null}
            </div>

            <dl className="mt-6 grid grid-cols-2 gap-4">
              <div className="border-l-2 pl-3" style={{ borderColor: "rgb(var(--c-session-1))" }}>
                <dt className="label-caps">Session 1</dt>
                <dd className="text-base font-semibold">
                  {fmtTime(c.session_1_start)} – {fmtTime(c.session_1_end)}
                </dd>
              </div>
              <div className="border-l-2 pl-3" style={{ borderColor: "rgb(var(--c-session-2))" }}>
                <dt className="label-caps">Session 2</dt>
                <dd className="text-base font-semibold">
                  {fmtTime(c.session_2_start)} – {fmtTime(c.session_2_end)}
                </dd>
              </div>
              <div>
                <dt className="label-caps">Operating hours</dt>
                <dd className="text-base">
                  {fmtTime(c.opens_at)} – {fmtTime(c.closes_at)}
                </dd>
              </div>
              {c.notes ? (
                <div>
                  <dt className="label-caps">Notes</dt>
                  <dd className="text-sm text-muted-foreground">
                    <Markdown text={c.notes} />
                  </dd>
                </div>
              ) : null}
            </dl>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[75vh] overflow-y-auto" data-testid="court-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {editingId ? "Edit court profile" : "New court profile"}
            </DialogTitle>
            <DialogDescription className="text-sm">
              Split-session boundaries, e.g. Session 1: 10:30 AM–1:30 PM, Session 2: 2:30 PM–4:30 PM.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-caps">Court name</Label>
                <Input
                  data-testid="court-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="rounded-sm h-12 text-base"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-caps">Location</Label>
                <Input
                  data-testid="court-location-input"
                  value={form.location || ""}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  className="rounded-sm h-12 text-base"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-caps">Opens</Label>
                <TimeSelect
                  value={form.opens_at}
                  onChange={(v) => setForm({ ...form, opens_at: v })}
                  testId="court-opens-select"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-caps">Closes</Label>
                <TimeSelect
                  value={form.closes_at}
                  onChange={(v) => setForm({ ...form, closes_at: v })}
                  testId="court-closes-select"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-caps">Session 1 start</Label>
                <TimeSelect
                  value={form.session_1_start}
                  onChange={(v) => setForm({ ...form, session_1_start: v })}
                  testId="court-s1-start-select"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-caps">Session 1 end</Label>
                <TimeSelect
                  value={form.session_1_end}
                  onChange={(v) => setForm({ ...form, session_1_end: v })}
                  testId="court-s1-end-select"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-caps">Session 2 start</Label>
                <TimeSelect
                  value={form.session_2_start}
                  onChange={(v) => setForm({ ...form, session_2_start: v })}
                  testId="court-s2-start-select"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-caps">Session 2 end</Label>
                <TimeSelect
                  value={form.session_2_end}
                  onChange={(v) => setForm({ ...form, session_2_end: v })}
                  testId="court-s2-end-select"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="label-caps">Notes</Label>
              <MarkdownField
                value={form.notes || ""}
                onChange={(v) => setForm({ ...form, notes: v })}
                testId="court-notes-input"
                minHeight={80}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full h-11" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button data-testid="court-save-btn" onClick={save} className="rounded-full h-11 px-6">
              {editingId ? "Save changes" : "Create court"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
